import { useEffect, useState } from "react";

export interface Profile {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
}

export interface AuthState {
  profile: Profile | null;
  loading: boolean;
}

/**
 * Whoever is signed in, from the platform's own endpoint.
 *
 * `/.auth/me` exists on every Static Web App with no configuration, and it is
 * the authoritative answer — the browser cannot fabricate one, because the
 * platform derives it from the session cookie rather than from anything the
 * page sends.
 *
 * A profile appears the moment someone signs in with any provider. That is
 * deliberately not the same as being allowed to use the chat endpoint: a
 * profile costs nothing, and each chat request spends money.
 */
export function useProfile(): AuthState {
  const [state, setState] = useState<AuthState>({ profile: null, loading: true });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const res = await fetch("/.auth/me");
        const body = (await res.json()) as { clientPrincipal: Profile | null };
        if (!cancelled) setState({ profile: body.clientPrincipal ?? null, loading: false });
      } catch {
        // Anonymous is the normal case, not an error worth surfacing.
        if (!cancelled) setState({ profile: null, loading: false });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** Providers the app offers. Twitter is disabled by a route rule. */
export const PROVIDERS = [
  { id: "aad", label: "Microsoft" },
  { id: "github", label: "GitHub" },
] as const;

export const loginUrl = (provider: string, returnTo = "/"): string =>
  `/.auth/login/${provider}?post_login_redirect_uri=${encodeURIComponent(returnTo)}`;

export const LOGOUT_URL = "/.auth/logout";

/** A short display name: the local part of an email, or the handle as given. */
export function displayName(profile: Profile): string {
  const details = profile.userDetails.trim();
  const at = details.indexOf("@");
  return at > 0 ? details.slice(0, at) : details;
}

/**
 * Storage key for per-user browser state.
 *
 * Portfolio holdings stay in localStorage and never reach the server, so
 * "provisioning" here means keeping one person's positions from appearing in
 * another's session on a shared machine — not creating a server-side account.
 * Anonymous visitors keep their own bucket, which carries over if they later
 * sign in on the same browser.
 */
export const storageKeyFor = (profile: Profile | null, base: string): string =>
  profile ? `${base}.${profile.userId}` : `${base}.anon`;
